import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { QuestionValidationTypes, ReduxState, AnswerItem, QuestionTypes, FormShowType } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import SLPInput, { SLPInputRef } from 'utilities/slp/input/input.index';
import SLPTextArea, { SLPTextAreaRef } from 'utilities/slp/textArea/textArea.index';
import { extractTitle } from 'scripts/string';
import { createQuestionTitle } from '../questionsTypes.script';
import { getQuestionBadge } from 'data/questionsBadge';

const SLPTextQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const isMultiLine =
        question.validations && question.validations.find((i) => i.title === QuestionValidationTypes.multiline)?.value;
    const inputRef = useRef<SLPInputRef>(null);
    const textareaRef = useRef<SLPTextAreaRef>(null);
    const [title, setTitle] = useState(question.title);
    const index = props.answerList?.findIndex((i) => i.question.id === question.id);

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        console.log('============');
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function lengthValidate(a: any) {
        return Number(question.validations?.find((i) => i.title === a)?.value);
    }

    function validate(e?: boolean) {
        if (isMultiLine) {
            const value = textareaRef.current?.getValue();
            if (question.is_required && !value) {
                if (e) textareaRef.current?.setError(lang.textEmp);
                return false;
            }
            if (value && !textareaRef.current?.getValidation()) {
                if (e) textareaRef.current?.setError(lang.textErr);
                return false;
            }
        } else {
            const value = inputRef.current?.getValue();
            if (question.is_required && !value) {
                if (e) inputRef.current?.setError(lang.textEmp);
                return false;
            }
            if (value && !inputRef.current?.getValidation()) {
                if (e) inputRef.current?.setError(lang.textErr);
                return false;
            }
        }
        return true;
    }

    function setAnswer(value?: string) {
        const data = [...props.answerList];
        if (value !== undefined && validate()) {
            const response: AnswerItem = {
                value: value,
                question: props.question,
                id: new Date().getTime(),
            };
            index !== undefined && index > -1 ? (data[index] = response) : data?.push(response);
        } else if (index !== undefined && index > -1) data.splice(index, 1);
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const value = isMultiLine ? textareaRef.current?.getValue() : inputRef.current?.getValue();
        if ((value || props.answerList?.find((i) => i.id === question.id)?.value) && validate(e) && props.answer)
            props.submit({
                value: value,
                question: question,
                id: new Date().getTime(),
                created_at: new Date().toISOString(),
            });
    }

    return (
        <>
            {isMultiLine ? (
                <SLPTextArea
                    minLength={lengthValidate(QuestionValidationTypes.minLength)}
                    maxLength={lengthValidate(QuestionValidationTypes.maxLength)}
                    length={lengthValidate(QuestionValidationTypes.length)}
                    hint={extractTitle(hint, false, false)}
                    ref={textareaRef}
                    counter={props.counter}
                    label={extractTitle(title, false, false)}
                    image={question.image_file}
                    color={props.form?.answer_color}
                    labelColor={props.form?.question_color}
                    required={question.is_required}
                    onChange={setAnswer}
                    formLang={props.form?.lang}
                    dir={props.form.direction}
                    defaultValue={
                        index !== undefined && index > -1 && props.answerList[index].value
                            ? props.answerList[index].value
                            : undefined
                    }
                    endIcon={getQuestionBadge(QuestionTypes.simple)}
                />
            ) : (
                <SLPInput
                    minLength={lengthValidate(QuestionValidationTypes.minLength)}
                    maxLength={lengthValidate(QuestionValidationTypes.maxLength)}
                    length={lengthValidate(QuestionValidationTypes.length)}
                    hint={extractTitle(hint, false, false)}
                    ref={inputRef}
                    counter={props.counter}
                    label={extractTitle(title, false, false)}
                    image={question.image_file}
                    color={props.form?.answer_color}
                    labelColor={props.form?.question_color}
                    required={question.is_required}
                    dir={props.form.direction}
                    onChange={setAnswer}
                    onEnter={() => {
                        if (props.form.show_type === FormShowType.single) props.next();
                    }}
                    formLang={props.form?.lang}
                    defaultValue={
                        index !== undefined && index > -1 && props.answerList[index].value
                            ? props.answerList[index].value
                            : undefined
                    }
                    endIcon={getQuestionBadge(QuestionTypes.simple)}
                />
            )}
        </>
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPTextQuestion));
